/**
 * AJ 2026-09-30: the module-level read cache every report port keeps, made
 * refreshable in place so a report tab left open can take a new sync without
 * a reload (useLiveReport in liveReport.ts drives it).
 *
 * get(key, load) is the old Map-of-promises cache: one in-flight read per
 * key, shared by every caller, a failed read dropped so the next caller
 * retries. It also remembers each key's loader, so refresh() can re-run the
 * reads already made for a tenant.
 *
 * refresh(match) re-reads every matching key in the background and swaps the
 * answers in only when ALL of them land; one failure keeps every old answer
 * (the page keeps showing what it had). A swap bumps `version`; data hooks
 * read it through useLiveCacheVersion and redraw from the (already settled)
 * cache, keeping their own state: only the data changes.
 *
 * invalidate(match) drops keys without re-reading them (on-demand reads like
 * a clicked day's detail: the next click reads fresh).
 */

export class LiveCache {
  #entries = new Map();
  #listeners = new Set();
  #ver = 0;

  get(key, load) {
    const hit = this.#entries.get(key);
    if (hit) return hit.promise;
    const entry = { promise: load(), load };
    entry.promise.then(
      (value) => {
        entry.settled = { value };
      },
      () => {
        // A failed read is not cached: the next caller retries it.
        if (this.#entries.get(key) === entry) this.#entries.delete(key);
      },
    );
    this.#entries.set(key, entry);
    return entry.promise;
  }

  /** The settled answer for `key`, or undefined while pending / unread. */
  peek(key) {
    return this.#entries.get(key)?.settled?.value;
  }

  has(key) {
    return this.#entries.has(key);
  }

  /**
   * Fold a change the page made itself into a cached answer (no redraw: the
   * page already shows it). The loader is kept, so a refresh re-reads it all.
   */
  update(key, fn) {
    const entry = this.#entries.get(key);
    if (!entry) return;
    const next = { promise: entry.promise.then(fn), load: entry.load };
    if (entry.settled) next.settled = { value: fn(entry.settled.value) };
    else
      next.promise.then(
        (value) => (next.settled = { value }),
        () => {},
      );
    this.#entries.set(key, next);
  }

  delete(key) {
    this.#entries.delete(key);
  }

  clear() {
    this.#entries.clear();
  }

  keys() {
    return [...this.#entries.keys()];
  }

  /** Drop matching keys; nothing is re-read until someone asks again. */
  invalidate(match) {
    for (const key of [...this.#entries.keys()]) if (match(key)) this.#entries.delete(key);
  }

  /**
   * Re-read every matching key; resolves with a commit() that swaps all the
   * answers in at once. Rejects (nothing swapped) on any failure. Split so
   * refreshTogether can land several caches as one swap.
   */
  async prepare(match) {
    const targets = [...this.#entries].filter(([key]) => match(key));
    if (targets.length === 0) return () => 0;
    const values = await Promise.all(targets.map(([, e]) => e.load()));
    return () => {
      targets.forEach(([key, e], i) => {
        this.#entries.set(key, {
          promise: Promise.resolve(values[i]),
          load: e.load,
          settled: { value: values[i] },
        });
      });
      this.#bump();
      return targets.length;
    };
  }

  /**
   * Re-read every matching key; swap all answers in at once, or none. Resolves
   * with the number of keys refreshed; rejects (swapping nothing) on any
   * failure.
   */
  async refresh(match) {
    return (await this.prepare(match))();
  }

  /** refresh() for every key carrying this tenant id (keys embed it). */
  refreshTenant(tenantId) {
    return this.refresh((key) => key.includes(tenantId));
  }

  subscribe = (listener) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  getVersion = () => this.#ver;

  #bump() {
    this.#ver += 1;
    for (const l of [...this.#listeners]) l();
  }
}

/**
 * Refresh one tenant's keys across several caches as a single swap: every
 * re-read lands first, then all are committed; any failure commits none.
 */
export async function refreshTogether(caches, tenantId) {
  const commits = await Promise.all(caches.map((c) => c.prepare((key) => key.includes(tenantId))));
  for (const commit of commits) commit();
}
